/**
 * The square mark beside the product name.
 *
 * Derived from the configured name rather than drawn, so `npm run set-name` moves the
 * logo with it. It was previously a hand-drawn "P" path — duplicated in the app shell and
 * on the sign-in page — which meant the mark still said Procurement after the product was
 * renamed, in two places that had to be found first.
 *
 * A two-letter monogram rather than a single initial: one letter in a small square reads
 * as a generic placeholder, two read as a wordmark. "Alpha's Requiso" gives AR,
 * "Requisition Hub" gives RH, and a single-word name like "Procurement" falls back to P
 * on its own, which is correct — PR would be inventing a word that is not there.
 */

interface Props {
  appName: string;
  /** Tailwind size class for the square, e.g. `size-7`. */
  className?: string;
}

/**
 * Skips the words nobody reads as part of an acronym, so "Board of Governors Purchasing"
 * gives BGP rather than BOG.
 */
const NOISE = new Set(['the', 'of', 'and', 'for', 'a', 'an']);

export function brandInitials(appName: string): string {
  const words = appName
    .split(/[\s\-–—_/]+/)
    // Apostrophes are part of a word, not a break: "Alpha's" is one word, not two.
    .map((w) => w.replace(/[^\p{L}\p{N}']/gu, ''))
    .filter((w) => w.length > 0 && !NOISE.has(w.toLowerCase()));

  if (words.length === 0) return '·';
  if (words.length === 1) return words[0]!.charAt(0).toUpperCase();
  return (words[0]!.charAt(0) + words[1]!.charAt(0)).toUpperCase();
}

export function BrandMark({ appName, className = 'size-7' }: Props) {
  const initials = brandInitials(appName);

  return (
    <span
      className={`grid ${className} shrink-0 place-items-center rounded bg-emerald-500 font-bold tracking-tight text-navy-950`}
      // The name is already rendered next to this in every use, so the mark itself is
      // decoration and repeating it to a screen reader is noise.
      aria-hidden
    >
      {/* Two letters need to be smaller than one to sit inside the same square. */}
      <span className={initials.length > 1 ? 'text-[11px]' : 'text-[13px]'}>{initials}</span>
    </span>
  );
}
